import { BaseModel } from '@/models/BaseModel';
import ColumnModel, { ContentAddon } from '@/components/layout/column/ColumnModel';
import { AddonsAdvancedSettingsModel } from '@/models/settings/AddonsAdvancedSettingsModel';
import { AddonEditState, AddonModelState } from '@/typings/interfaces/state/Addon';
import { CustomCSSModel } from '@/models/CustomCSSModel';
import { getDeviceData } from '@/hooks/useDevice';
import { CustomCSS } from '@/typings/interfaces/data/settings/settings';
import { createEditFormUrl, generateUniqueId } from '@/utilities/Utilities';
import store from '@/store';
import { SectionType } from '@/typings/enums/enums';
import useAxios from '@/hooks/useAxios';
import { ConfigData } from '@/Config';

/**
 * Author: Dannie Hansen | Sebastian Jakobsen | Nicky Christensen
 */
export default abstract class AddonModel<Data, State> extends BaseModel<Data & ContentAddon, State & AddonModelState> {
  public readonly campaignId: number;
  public column: ColumnModel;
  public alias: string;

  private static reservedIds: string[] = [];
  private deleted = false;

  constructor(data: Data & ContentAddon, column: ColumnModel) {
    super(data);
    this.column = column;
    this.campaignId = column.campaignId;
    this.alias = data.alias;

    // Conflict found or no addon id exists. Some other addon already uses this ID.
    // We need to refresh it to avoid conflicts with style or other functionality.
    if (!data.id || AddonModel.reservedIds.includes(data.id)) {
      data.id = generateUniqueId();
    }

    AddonModel.reservedIds.push(data.id);

    this.parse(data);
  }

  parse(data: Data & ContentAddon) {
    const state = this.state;
    state.modelId = this.modelId;
    state.alias = data.alias;

    // Constructor handles if no data id exists. But to satisfy the typing we need to also handle it here..
    state.id = data.id ?? generateUniqueId();
    state.classIdentifier = `grid__addon--${state.id}`;
    state.addonValid = this.isAddonValid();

    const editState = store.getters['campaign/state'].edit;
    if (!state.edit && editState?.enabled) {
      state.edit = {} as AddonEditState;
      state.edit.icon = '';
      state.edit.label = data.label ?? '';
      state.edit.isActive = false;
      state.edit.isFocus = false;
      state.edit.title = this.getAddonTitle();
    }

    if (data?.settings?.advanced) {
      if (state.genericAdvancedSettings) {
        state.genericAdvancedSettings.setData(data.settings.advanced);
      } else {
        state.genericAdvancedSettings = new AddonsAdvancedSettingsModel(data.settings.advanced);
      }
    }

    if (data.settings?.advanced?.generic?.customcss) {
      state.customCss = AddonModel.parseCustomCSSData(data, state.classIdentifier);
    }

    this.parseAddon(data);
  }

  public isAddonValid(): boolean {
    return true;
  }

  public getEditUrl() {
    const editFormUrl = `/edit/campaign/addon/${this.campaignId}/${this.column.sectionId}?row_index=${this.column.rowIndex}&col_index=${this.column.index}&addon_index=${this.index}`;
    return createEditFormUrl(editFormUrl);
  }

  public getAddonTitle(): string {
    const addonsData = store.getters['global/addonsData'];
    if (addonsData) {
      return addonsData[this.state.alias]?.name;
    }
    return this.state.alias;
  }

  public abstract parseAddon(data: Data): void;

  public getValueRoot(): string {
    return 'settings';
  }

  public getSection() {
    return this.column.row.section;
  }

  canDelete(): boolean {
    return this.column.row.section.canDelete();
  }

  canOpenSectionSettings() {
    return this.getSection().getSectionType() === SectionType.FLOWPAGE;
  }

  canOpenGameSettings() {
    return this.getSection().getSectionType() === SectionType.FLOWPAGE;
  }

  openSectionSettings() {
    this.getSection().openSectionSettings();
  }

  canCopy(): boolean {
    return this.state.alias.indexOf('game') === -1;
  }

  canDuplicate(): boolean {
    return !!(this.index || (this.index === 0 && this.column));
  }

  canPaste(): boolean {
    const clipboardState = store.getters['clipboard/state'];
    const activeModel = store.getters['editing/activeModel'];
    const modelSection = activeModel?.getSection();
    if (modelSection?.state?.config?.hasRegistrationAddon) {
      return false;
    }
    return clipboardState.elementInClipboard instanceof AddonModel;
  }

  canMoveUp(): boolean {
    return this.index > 0;
  }

  onMoveUp() {
    this.column.onMoveAddonUp(this.index);
  }

  canMoveDown(): boolean {
    const col = this.column;
    const colState = col.state;
    return this.index < colState.addons.length - 1;
  }

  onMoveDown() {
    this.column.onMoveAddonDown(this.index);
  }

  onPasteElement() {
    const addonData = store.getters['clipboard/data'];
    if (addonData) {
      this.column.onPasteAddon(addonData, this.index);
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  async onDelete() {}

  //Delete addon
  async delete() {
    if (this.deleted) {
      return;
    }

    this.deleted = true;
    await this.column.deleteAddon(this.index);
  }

  async saveBlock() {
    return this.column.row.section.saveBlock();
  }

  private static parseCustomCSSData(data: ContentAddon, identifier: string): CustomCSSModel | undefined {
    if (data.settings?.advanced?.generic?.customcss) {
      const useData = getDeviceData(data.settings.advanced.generic.customcss) as CustomCSS | undefined;

      if (!useData) {
        return undefined;
      }
      if (useData.code && useData.code.length > 0) {
        return AddonModel.constructCustomCSSState(useData, identifier);
      }
      return undefined;
    }
  }

  private static constructCustomCSSState(data: CustomCSS, identifier: string): CustomCSSModel {
    return new CustomCSSModel(data, identifier);
  }

  public shallSkipInitialParse(): boolean {
    return true;
  }

  public setEditingActive(): void {
    if (this.state.edit) {
      this.state.edit.isActive = true;
      this.state.edit.isCollapsed = false;
    }

    if (this.column.state.edit) {
      this.column.state.edit.isCollapsed = false;
    }
    if (this.column.row.state.edit) {
      this.column.row.state.edit.isCollapsed = false;
    }

    if (this.column.row.section.state.edit) {
      this.column.row.section.state.edit.isCollapsed = false;
    }
  }

  // eslint-disable-next-line @typescript-eslint/ban-types
  public async callAddonHook<ResponseData>(hook: string, formData?: object): Promise<ResponseData> {
    const campaignModel = store.getters['campaign/model'];
    const campaignState = campaignModel.state;
    const hookUrl = `${
      campaignState.config?.apiEndpoint ?? ConfigData.apiEndpoint
    }/api/v1/campaign/addonHook?hook=${hook}&campaign_id=${campaignModel.id}&page_id=${
      this.getSection().id
    }&row_index=${this.column.row.index}&col_index=${this.column.index}&addon_index=${this.index}`;

    const { postDataFormData } = useAxios<ResponseData>(hookUrl, formData);

    return await postDataFormData();
  }

  public get index() {
    // @ts-ignore
    const index = this.column.state.addons.indexOf(this);

    if (index === undefined) {
      throw new Error(`Could not find addon index in column addons`);
    }

    return index;
  }
}
